import {
  Button,
  Center,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import NextImage from 'next/image';
import { useEffect, useState } from 'react';
import { sendAnalyticsEvent } from '../common/libs/googleAnalytics';
import { useAppContext } from '../contexts/app';
import useIdApiClient from '../hooks/useIdApiClient';

export const PasswordAuthInduction: React.FC = () => {
  const { firebaseUser, isAuthenticated } = useAppContext();
  const { proceedIdPage } = useIdApiClient();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [currentProvider, setCurrentProvider] = useState('');

  /**
   * 認証状況に応じて表示画面を切り替え
   */
  useEffect(() => {
    if (isAuthenticated) {
      (async () => {
        if (firebaseUser) {
          const hasPasswordAuth = firebaseUser.providerData.some(
            (provider) => provider.providerId === 'password',
          );
          if (hasPasswordAuth) {
            // 認証方法変更が必要な場合
            if (firebaseUser.providerData.length >= 2) {
              if (firebaseUser.emailVerified) {
                // パスワード認証設定済みで、他の認証方法が未削除の場合はログイン方法変更完了画面を表示
                await proceedIdPage(
                  `/users/change-login-method-completed?from_service=slide`,
                );
              } else {
                // メールアドレス確認画面を表示
                await proceedIdPage(
                  `/users/change-login-method-verify-email?from_service=slide`,
                );
              }
            }
          } else {
            const providerId = firebaseUser.providerData[0]?.providerId ?? '';
            setCurrentProvider(
              providerId === 'facebook.com'
                ? 'facebook'
                : providerId === 'apple.com'
                ? 'apple'
                : '',
            );
            setIsOpenModal(true);
          }
        }
      })();
    }
    // NOTE: proceedIdPageは依存配列に含めるべきだが、useCallbackでメモ化されていないため、無限ループを避けるために除外している
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseUser, isAuthenticated]);

  const handleCloseModal = () => {
    setIsOpenModal(false);
  };

  return (
    <PasswordAuthInductionModal
      isOpen={isOpenModal}
      onClose={handleCloseModal}
      currentProvider={currentProvider}
    />
  );
};

type PasswordAuthInductionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  currentProvider: string;
};
const PasswordAuthInductionModal: React.FC<PasswordAuthInductionModalProps> = (
  props,
) => {
  const { proceedIdPage } = useIdApiClient();

  useEffect(() => {
    sendAnalyticsEvent('password_auth_induction', 'show', null);
  }, []);

  const handleClose = () => {
    sendAnalyticsEvent('password_auth_induction', 'click', 'close');
    props.onClose();
  };

  return (
    <Modal
      size={'md'}
      blockScrollOnMount={false}
      isOpen={props.isOpen}
      onClose={handleClose}
    >
      <ModalOverlay />
      <ModalContent borderRadius="lg">
        <ModalHeader>
          <Center>
            <HStack>
              <NextImage
                src="/images/icon_antaa_white_small.png"
                width={56}
                height={56}
              />
              <Text fontSize={'xx-large'} fontWeight={'bold'}>
                Antaa
              </Text>
            </HStack>
          </Center>
          <VStack spacing={0}>
            <Text fontSize={'xl'} fontWeight={'bold'}>
              ログイン変更のお願い
            </Text>
          </VStack>
        </ModalHeader>

        <ModalBody mx="20px">
          <Text>
            セキュリティ強化および個人情報保護対策の一環としてFacebookおよびApple
            IDによるログインを廃止させていただくことになりました。誠に恐れ入りますが、メールアドレスによるログインへの変更をお願い致します。
          </Text>
          <Text>
            2026年1月26日以降、Facebookによるログインが出来なくなる可能性があります。
            <b>
              <u>Facebookログインをご利用の方は早めの移行をお願い致します。</u>
            </b>
          </Text>

          <Center>
            <VStack w="full" spacing={5} mt={10} mb={10}>
              <Button
                colorScheme="brand"
                fontSize="md"
                w="full"
                borderRadius="20px"
                onClick={async (e) => {
                  e.preventDefault();
                  sendAnalyticsEvent(
                    'password_auth_induction',
                    'click',
                    'proceed',
                  );
                  props.onClose();
                  await proceedIdPage(
                    `/users/change-login-method?current_provider=${props.currentProvider}&from_service=slide`,
                  );
                }}
              >
                メールログインに切り替える
              </Button>
              <Text fontWeight={'bold'} cursor="pointer" onClick={handleClose}>
                あとで行う
              </Text>
            </VStack>
          </Center>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
